import { forwardRef, useEffect, useRef, useState } from "react";

export interface AudioVisualizerProps {
  /** Audio to visualise. Nothing is drawn until it decodes. */
  blob: Blob | null;
  width: number;
  height: number;
  /** Width of each bar in px. */
  barWidth?: number;
  /** Gap between bars in px. */
  gap?: number;
  /** Colour of not-yet-played bars. */
  barColor?: string;
  /** Colour of played bars — only applies when currentTime/duration are given. */
  barPlayedColor?: string;
  /** Canvas background; transparent when omitted. */
  backgroundColor?: string;
  /** Playback position in seconds, used to colour the played portion. */
  currentTime?: number;
  /** Total duration in seconds. Falls back to the decoded buffer's duration. */
  duration?: number;
  className?: string;
}

/**
 * Waveform visualiser for a recorded/fetched audio Blob.
 *
 * Replaces the `react-audio-visualize` package, which vendored its own copy of
 * React 17's JSX runtime and therefore crashed under React 19 (it read
 * `__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED`, removed in 19). The prop
 * signature mirrors the parts of that library this app used, so call sites did
 * not have to change.
 *
 * Decoding happens once per blob via the Web Audio API; drawing is a cheap
 * canvas pass that re-runs when playback position or styling changes.
 */
const AudioVisualizer = forwardRef<HTMLCanvasElement, AudioVisualizerProps>(
  (
    {
      blob,
      width,
      height,
      barWidth = 2,
      gap = 1,
      barColor = "#000000",
      barPlayedColor,
      backgroundColor = "transparent",
      currentTime,
      duration,
      className,
    },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [peaks, setPeaks] = useState<number[]>([]);
    const [decodedDuration, setDecodedDuration] = useState(0);

    // Expose the canvas through the forwarded ref while keeping our own handle.
    const setCanvas = (node: HTMLCanvasElement | null) => {
      canvasRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Decode the blob into a fixed number of amplitude buckets — one per bar.
    useEffect(() => {
      if (!blob) {
        setPeaks([]);
        setDecodedDuration(0);
        return;
      }

      let cancelled = false;
      const barCount = Math.max(
        1,
        Math.floor(width / Math.max(1, barWidth + gap))
      );

      const decode = async () => {
        let audioContext: AudioContext | undefined;
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const AudioContextCtor =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext;
          if (!AudioContextCtor) return;

          audioContext = new AudioContextCtor();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          if (cancelled) return;

          const samples = audioBuffer.getChannelData(0);
          const bucketSize = Math.floor(samples.length / barCount) || 1;
          const next: number[] = [];
          let max = 0;

          for (let bar = 0; bar < barCount; bar++) {
            const start = bar * bucketSize;
            let sum = 0;
            let counted = 0;
            for (let i = start; i < start + bucketSize && i < samples.length; i++) {
              sum += Math.abs(samples[i]);
              counted++;
            }
            const value = counted ? sum / counted : 0;
            next.push(value);
            if (value > max) max = value;
          }

          // Normalise so quiet clips still fill the canvas.
          setPeaks(max > 0 ? next.map((v) => v / max) : next);
          setDecodedDuration(audioBuffer.duration);
        } catch {
          // Undecodable/empty blob: render nothing rather than breaking the page.
          if (!cancelled) {
            setPeaks([]);
            setDecodedDuration(0);
          }
        } finally {
          // Not every implementation returns a promise from close().
          try {
            const closed = audioContext?.close() as Promise<void> | undefined;
            if (closed && typeof closed.catch === "function") {
              closed.catch(() => undefined);
            }
          } catch {
            // Already closed — nothing to release.
          }
        }
      };

      void decode();
      return () => {
        cancelled = true;
      };
    }, [blob, width, barWidth, gap]);

    // Draw (and redraw as playback advances).
    useEffect(() => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;

      context.clearRect(0, 0, width, height);
      if (backgroundColor !== "transparent") {
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, width, height);
      }
      if (!peaks.length) return;

      const total = duration ?? decodedDuration;
      const playedRatio =
        barPlayedColor && currentTime !== undefined && total > 0
          ? Math.min(1, Math.max(0, currentTime / total))
          : 0;
      const step = barWidth + gap;
      const centre = height / 2;

      peaks.forEach((peak, index) => {
        // Keep a 1px floor so silence still reads as a waveform.
        const barHeight = Math.max(1, peak * height);
        const x = index * step;
        const played = playedRatio > 0 && x / width < playedRatio;
        context.fillStyle = played ? (barPlayedColor as string) : barColor;
        context.fillRect(x, centre - barHeight / 2, barWidth, barHeight);
      });
    }, [
      peaks,
      decodedDuration,
      width,
      height,
      barWidth,
      gap,
      barColor,
      barPlayedColor,
      backgroundColor,
      currentTime,
      duration,
    ]);

    return (
      <canvas
        ref={setCanvas}
        width={width}
        height={height}
        className={className}
        data-testid="audio-visualizer"
      />
    );
  }
);

AudioVisualizer.displayName = "AudioVisualizer";

export default AudioVisualizer;
